import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Table, TableColumn } from "@/components/Table/Table";
import { classNames } from "@/utils/classNames";
import { useTableSearch } from "@/utils";
import { Order, SortParams } from "@/types/types";
import { useAppSelector } from "@/redux/hooks";

import { useGetSupportCompaniesQuery } from "@/pages/Private/redux/support-dashboard/support-dashboard.api";
import { supportDashboardSelector } from "@/pages/Private/redux/support-dashboard/support-dashboard.slice";
import { useGetCompanyTicketCountsQuery } from "@/pages/Private/redux/tickets/tickets-admin.api";

import { SupportCompany } from "../schema/support-dashboard";
import {
	formatActivityDate,
	formatLastActive,
	getActivitySummary,
	getSendErrorBadgeClasses,
	getSubscriptionBadgeClasses,
	sumField,
	TicketCounts,
	ticketCountsByCompany,
} from "../utils/support-dashboard.utils";
import { ExpandedCompanyRow } from "./ExpandedCompanyRow";
import { OpenTicketsCell } from "./OpenTicketsCell";
import { SendErrorRangeSelect } from "./SendErrorRangeSelect";
import { SendErrorsKpiStrip } from "./SendErrorsKpiStrip";

const useColumns = (
	ticketCounts: Map<number, TicketCounts>,
	lastRolledUpDate: string | null
): TableColumn[] => {
	const { t } = useTranslation();

	return useMemo(
		() => [
			{
				title: t("supportDashboard.columns.companyName"),
				uid: "name",
				orderTitle: "name",
				render: (item: SupportCompany) => (
					<span className="text-sm font-medium text-gray-900">{item.name}</span>
				),
			},
			{
				title: t("supportDashboard.columns.lastActivity"),
				uid: "lastActivityDate",
				render: (item: SupportCompany) => (
					<span
						className="text-sm text-text-meta whitespace-nowrap"
						title={
							lastRolledUpDate
								? t("supportDashboard.activity.lastActivityTooltip", {
										date: formatActivityDate(lastRolledUpDate),
									})
								: undefined
						}
					>
						{formatActivityDate(item.lastActivityDate)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.lastActive"),
				uid: "lastActiveAt",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta whitespace-nowrap">
						{formatLastActive(item.lastActiveAt)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.activity"),
				uid: "activityStats",
				render: (item: SupportCompany) => (
					<span
						className="text-sm text-text-meta whitespace-nowrap"
						title={
							lastRolledUpDate
								? t("supportDashboard.activity.activityTooltip", {
										date: formatActivityDate(lastRolledUpDate),
									})
								: undefined
						}
					>
						{getActivitySummary(item.activityStats, lastRolledUpDate, t)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.openScraping"),
				uid: "hasOpenScrapingRequest",
				render: (item: SupportCompany) => (
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							item.hasOpenScrapingRequest
								? "bg-status-success-light text-status-success"
								: "bg-inactive-item text-text-meta"
						)}
					>
						{item.openScrapingRequests.length}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.runningCampaigns"),
				uid: "hasRunningCampaign",
				orderTitle: "runningCampaigns",
				render: (item: SupportCompany) => (
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							item.hasRunningCampaign
								? "bg-status-success-light text-status-success"
								: "bg-inactive-item text-text-meta"
						)}
					>
						{item.runningCampaignCount}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.errors24h"),
				uid: "sendErrors",
				orderTitle: "sendErrors",
				render: (item: SupportCompany) => (
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							getSendErrorBadgeClasses(item)
						)}
					>
						{item.sendErrors.needsAttention > 0
							? item.sendErrors.needsAttention
							: item.sendErrors.total}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.openTickets"),
				uid: "openTickets",
				render: (item: SupportCompany) => (
					<OpenTicketsCell companyId={item.id} counts={ticketCounts.get(item.id)} />
				),
			},
			{
				title: t("supportDashboard.columns.messagesToday"),
				uid: "messagesToday",
				orderTitle: "messagesToday",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">{item.messagesToday}</span>
				),
			},
			{
				title: t("supportDashboard.columns.subscriptionType"),
				uid: "subscriptionType",
				render: (item: SupportCompany) => (
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							getSubscriptionBadgeClasses(item.subscriptionType)
						)}
					>
						{t(`supportDashboard.subscriptionType.${item.subscriptionType}`)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.billingInterval"),
				uid: "billingInterval",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">
						{item.billingInterval
							? t(`supportDashboard.billingInterval.${item.billingInterval}`)
							: "—"}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.creditsPerCommitment"),
				uid: "creditsPerCommitmentPeriod",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">
						{item.creditsPerCommitmentPeriod === null ||
						item.creditsPerCommitmentPeriod === undefined
							? "—"
							: String(item.creditsPerCommitmentPeriod)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.availableCredits"),
				uid: "availableCredits",
				render: (item: SupportCompany) => (
					<span
						className={classNames(
							"inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
							item.lowOnCredits
								? "bg-status-error-light text-status-error"
								: "bg-inactive-item text-text-meta"
						)}
					>
						{Math.round(item.availableCredits)} / {Math.round(item.creditAllowance)}
					</span>
				),
			},
			{
				title: t("supportDashboard.columns.emailsToday"),
				uid: "emailsSentToday",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">{sumField(item, "emailsSentToday")}</span>
				),
			},
			{
				title: t("supportDashboard.columns.liInvitesToday"),
				uid: "linkedinInvitesToday",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">{sumField(item, "linkedinInvitesToday")}</span>
				),
			},
			{
				title: t("supportDashboard.columns.liMessagesToday"),
				uid: "linkedinMessagesToday",
				render: (item: SupportCompany) => (
					<span className="text-sm text-text-meta">{sumField(item, "linkedinMessagesToday")}</span>
				),
			},
		],
		[t, ticketCounts, lastRolledUpDate]
	);
};

const renderExpandedRow = (lastRolledUpDate: string | null) => (item: SupportCompany) => (
	<ExpandedCompanyRow company={item} lastRolledUpDate={lastRolledUpDate} />
);

export const SupportDashboardTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } = useTableSearch();
	const [filterOpenScraping, setFilterOpenScraping] = useState(false);
	const [filterRunningCampaign, setFilterRunningCampaign] = useState(false);
	const [filterSendErrors, setFilterSendErrors] = useState(false);
	const [filterLowOnCredits, setFilterLowOnCredits] = useState(false);
	const [sorting, setSorting] = useState<SortParams>({
		sortBy: "",
		sortOrder: Order.DESC,
	});

	// PREC-3939: a client-side join — the counts endpoint carries ids only, the rows the names.
	const { data: ticketCountRows } = useGetCompanyTicketCountsQuery();
	const ticketCounts = useMemo(() => ticketCountsByCompany(ticketCountRows), [ticketCountRows]);
	const { companies, meta, sendErrorRange, lastRolledUpDate } =
		useAppSelector(supportDashboardSelector);
	const columns = useColumns(ticketCounts, lastRolledUpDate);

	const { isLoading, isFetching } = useGetSupportCompaniesQuery({
		page,
		limit,
		searchValue: query || undefined,
		sortBy: sorting.sortBy || undefined,
		sortOrder: sorting.sortOrder,
		hasOpenScrapingRequest: filterOpenScraping ? "true" : undefined,
		hasRunningCampaign: filterRunningCampaign ? "true" : undefined,
		hasSendErrors: filterSendErrors ? "true" : undefined,
		lowOnCredits: filterLowOnCredits ? "true" : undefined,
		sendErrorRange,
	});

	const handleSorting = useCallback(
		(sortParams: SortParams) => {
			setPage(1);
			setSorting({
				sortBy: sortParams.sortBy || "",
				sortOrder: sortParams.sortOrder || Order.DESC,
			});
		},
		[setPage]
	);

	const handleChangeRowsPerPage = useCallback(
		(newLimit: number) => {
			setLimit(newLimit);
			setPage(1);
		},
		[setLimit, setPage]
	);

	useEffect(() => {
		setPage(1);
	}, [filterOpenScraping, filterRunningCampaign, filterSendErrors, filterLowOnCredits]);

	const totalPages = meta ? Math.ceil(meta.itemCount / limit) : 1;

	if (companies.length === 0 && isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div className="w-full">
			<SendErrorRangeSelect />
			<SendErrorsKpiStrip />

			{/* Search + Filters */}
			<div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-end">
				<div className="flex-grow max-w-md">
					<InputField
						handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
						name="search"
						placeholder={t("supportDashboard.searchPlaceholder")}
						value={searchValue}
					/>
				</div>
				<div className="flex items-center gap-6 mb-3">
					<Checkbox
						isChecked={filterOpenScraping}
						onChange={() => setFilterOpenScraping((v) => !v)}
					>
						{t("supportDashboard.filters.openScrapingRequests")}
					</Checkbox>
					<Checkbox
						isChecked={filterRunningCampaign}
						onChange={() => setFilterRunningCampaign((v) => !v)}
					>
						{t("supportDashboard.filters.runningCampaigns")}
					</Checkbox>
					<Checkbox isChecked={filterSendErrors} onChange={() => setFilterSendErrors((v) => !v)}>
						{t("supportDashboard.filters.hasSendErrors")}
					</Checkbox>
					<Checkbox
						isChecked={filterLowOnCredits}
						onChange={() => setFilterLowOnCredits((v) => !v)}
					>
						{t("supportDashboard.filters.lowOnCredits")}
					</Checkbox>
				</div>
			</div>

			<p className="mb-2 text-xs text-text-meta">
				{lastRolledUpDate
					? t("supportDashboard.activity.freshness", {
							date: formatActivityDate(lastRolledUpDate),
						})
					: t("supportDashboard.activity.freshnessNone")}
			</p>

			<Table
				columns={columns}
				data={companies}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleFetchPage={setPage}
				handleSorting={handleSorting}
				hideFooter
				isFetching={isFetching}
				isLoading={isLoading}
				limit={limit}
				noDataText={t("supportDashboard.noData")}
				overallCount={meta?.itemCount}
				page={page}
				renderExpandedRow={renderExpandedRow(lastRolledUpDate)}
				totalPages={totalPages}
			/>
		</div>
	);
};
